//@flow
import * as React from 'react';
import md5 from 'md5';
import {
  Table,
  TableRow,
  TableHead,
  TableCell,
  Typography,
  TableBody,
  TableFooter,
  Paper,
  TextField
} from '@mui/material';

import green from '@mui/material/colors/green';
import grey from '@mui/material/colors/grey';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/StarBorder';
import { useMutation } from '../../hooks/useMutation';
import Button from '../button';
import { Stripe } from '../../stripe';

// enum ListingTypes {
// 'STANDARD' = 'STANDARD',
// 'PLUS' = 'PLUS' ,
// 'PREMIUM' = 'PREMIUM'
// }

type Actions = {
  //TODO: when typescript is supported add enum ListingTypes to value
  value: string,
  amount: number
};

type Row = {
  info: string,
  standard: string | boolean,
  plus: string | boolean
};
type Column = {
  label: string,
  name: string,
  hide: boolean,
  feat: string
};

type Props = {
  onPayment: (opts: Object) => void,
  actions: Actions[],
  rows: Row[],
  columns: Column[],
  mutationOptions: { token: string }
};

const UPDATE_PAYMENT_INFO = `
mutation($amount: Int!, $product: String!, $dwelling: ID!) {
  updatePaymentInfo(amount: $amount, product: $product, dwelling: $dwelling)
}
`;

const PriceTable: React.FC<Props> = (props) => {
  const { mutate: UpdatePaymentInfo } = useMutation(UPDATE_PAYMENT_INFO);
  const [plusCode, setPlusCode] = React.useState('');
  const [premiumCode, setPremiumCode] = React.useState('');
  const [plusPrice, setPlusPrice] = React.useState(0);
  const [premiumPrice, setPremiumPrice] = React.useState(0);
  const [paymentInfo, setPaymentInfo] = React.useState('');
  const [finalAmount, setFinalAmount] = React.useState(0);
  const [reload, setReload] = React.useState(false);
  const targetElement = React.useRef();

  const onClickHandler = async ({ amount, value: product }, id) => {
    const plusDiscount = product === 'PLUS' && plusPrice;
    const premiumDiscount = product === 'PREMIUM' && premiumPrice;
    const discounted = plusDiscount || premiumDiscount || amount;
    const _amount = discounted * 100;

    if (targetElement.current) {
      targetElement.current.scrollIntoView({
        behavior: 'smooth'
      });
    }

    if (paymentInfo && _amount === finalAmount) {
      return;
    }
    if (paymentInfo) {
      setReload(true);
    }
    const res = await UpdatePaymentInfo({
      amount: _amount,
      product,
      dwelling: id
    });
    setPaymentInfo(res.data.updatePaymentInfo);
    setFinalAmount(_amount);
    setReload(false);
    if (targetElement.current) {
      targetElement.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const discountHandler = (value, { amount, value: product, discounts }) => {
    const hash = md5(JSON.stringify({ secret: value }));
    const plusDiscount =
      product === 'PLUS' && discounts?.find((d) => hash === d.code);

    const premiumDiscount =
      product === 'PREMIUM' && discounts?.find((d) => hash === d.code);

    let discounted = amount;
    if (plusDiscount?.flat || premiumDiscount?.flat) {
      discounted -= plusDiscount?.flat || premiumDiscount?.flat;
    } else if (plusDiscount?.percentage || premiumDiscount?.percentage) {
      discounted *= plusDiscount?.percentage || premiumDiscount?.percentage;
    }

    if (product === 'PLUS') {
      setPlusCode(value);
      if (discounted !== amount) {
        setPlusPrice(discounted);
      } else {
        setPlusPrice(0);
      }
    } else if (product === 'PREMIUM') {
      setPremiumCode(value);
      if (discounted !== amount) {
        setPremiumPrice(discounted);
      } else {
        setPremiumPrice(0);
      }
    }
  };

  function renderIcon(value) {
    if (value === true) {
      return (
        <CheckCircleIcon fontSize="default" style={{ color: green[500] }} />
      );
    } else if (value === false) {
      return <RemoveIcon fontSize="default" style={{ color: grey[500] }} />;
    }
    return value;
  }

  const cellWith = '100px';
  return (
    <div>
      <Paper style={{ marginTop: '20px' }} elevation={2}>
        <div
          style={{
            textAlign: 'center',
            padding: '1em 0',
            marginBottom: '1em',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            width: '100%',
            backgroundColor: '#ef6c02'
          }}
        >
          <Typography
            id="listingType"
            variant="h5"
            component="h5"
            style={{ color: '#fff' }}
          >
            {props.title || 'Vælg annoncering'}
          </Typography>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {props.columns.map(({ name, label, feat, hide }) => {
                  return (
                    <TableCell
                      style={{ border: 'none', width: cellWith }}
                      key={name}
                      size="small"
                      align="center"
                    >
                      {hide ? null : (
                        <Typography
                          variant="h6"
                          component="p"
                          color="textPrimary"
                        >
                          {label}
                          {feat && <StarIcon />}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
              <TableRow>
                {props.columns.map(({ name, feat }) => {
                  return (
                    <TableCell
                      key={name}
                      style={{ paddingBottom: '13px', width: cellWith }}
                      padding="none"
                      size="small"
                      align="center"
                    >
                      {feat ? (
                        <Typography
                          variant="caption"
                          component="p"
                          color="textSecondary"
                        >
                          {feat}
                        </Typography>
                      ) : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {props.rows.map((row) => {
                return (
                  <TableRow key={row.info}>
                    {props.columns.map(({ name }, idx) => {
                      return name === 'info' ? (
                        <TableCell
                          key={`${row.info}_${idx}`}
                          size="small"
                          style={{
                            borderRight: '2px solid #e0e0e0',
                            width: cellWith
                          }}
                        >
                          {row.info}
                        </TableCell>
                      ) : (
                        <TableCell
                          key={`${row.info}_${idx}`}
                          style={{ width: cellWith }}
                          size="small"
                          align="center"
                        >
                          {renderIcon(row[name])}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {props.actions ? (
                <TableRow key={'discountRow'}>
                  <TableCell
                    key={`discount_title`}
                    size="small"
                    style={{
                      borderRight: '2px solid #e0e0e0',
                      width: cellWith
                    }}
                  >
                    {'Aktivere rabatkode'}
                  </TableCell>
                  {props.actions.map((action, idx) => {
                    if (action === null) {
                      return <TableCell key={idx} />;
                    }
                    if (action.value === 'STANDARD') {
                      return <TableCell key={idx} />;
                    }
                    const code =
                      action.value === 'PLUS' ? plusCode : premiumCode;

                    return (
                      <TableCell key={action.value + '_discount'} align="center">
                        <TextField
                          onChange={(e) =>
                            discountHandler(e.target.value, action)
                          }
                          variant="outlined"
                          value={code}
                          size={'small'}
                          style={{ marginLeft: '6px', marginRight: '6px' }}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ) : null}
              {props.actions && (plusCode || premiumCode) ? (
                <TableRow key={'discount_price'}>
                  <TableCell
                    key={`discount_price_title`}
                    size="small"
                    style={{
                      borderRight: '2px solid #e0e0e0',
                      width: cellWith
                    }}
                  >
                    {'Rabat pris'}
                  </TableCell>
                  {props.actions.map((action, idx) => {
                    if (action === null) {
                      return <TableCell key={idx} />;
                    }
                    if (action.value === 'STANDARD') {
                      return <TableCell key={idx} />;
                    }
                    const price =
                      action.value === 'PLUS'
                        ? plusPrice
                          ? `${plusPrice} kr.`
                          : plusCode
                          ? 'Ugyldig kode'
                          : '-'
                        : premiumPrice
                        ? `${premiumPrice} kr.`
                        : premiumCode
                        ? 'Ugyldig kode'
                        : '-';

                    return (
                      <TableCell key={action.value + '_discount'} align="center">
                        {price}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ) : null}
            </TableBody>
            {props.actions && (
              <TableFooter>
                <TableRow>
                  <TableCell style={{ borderRight: '2px solid #e0e0e0' }} />
                  {props.actions.map((action, idx) => {
                    if (action === null) {
                      return <TableCell key={idx} />;
                    }
                    const selected = action.value === props.listingType;
                    return (
                      <TableCell key={action.value} align="center">
                        <Button
                          style={
                            selected
                              ? {}
                              : {
                                  backgroundColor: green[500],
                                  color: '#fff'
                                }
                          }
                          fullWidth
                          disabled={selected}
                          variant={selected ? 'outlined' : 'contained'}
                          label={action.label}
                          onClick={() => onClickHandler(action, props.id)}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>
        <div style={{ padding: '1em' }}>{props.info}</div>
      </Paper>
      {paymentInfo && !reload ? (
        <Paper
          ref={targetElement}
          style={{ marginTop: '20px', padding: '20px' }}
        >
          <Stripe paymentInfo={paymentInfo} amount={finalAmount} />
        </Paper>
      ) : null}
    </div>
  );
};

export default PriceTable;